<template>
    <div>
        <v-overlay :value="loadingBulkPaymentsHistoryList">
            <v-progress-circular indeterminate size="64"></v-progress-circular>
        </v-overlay>
        <v-card class="col-item" flat style="border-top: 1px solid lightgray">
            <v-simple-table v-if="user.role === 'admin'" id="search">
                <template v-slot:default>
                    <thead>
                    <tr>
                        <th colspan="2">Date</th>
                        <th>Status</th>
                        <th>Company name</th>
                        <th>Bank name</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr>
                        <td>
                            <vc-date-picker v-model="startDate" :max-date="new Date()" color="gray">
                                <template v-slot="{ inputValue, inputEvents }">
                                    <v-text-field
                                            class="mt-2 description"
                                            :value="inputValue ? `${inputValue}` : null"
                                            v-on="inputEvents"
                                            dense
                                            outlined
                                    />
                                </template>
                            </vc-date-picker>
                        </td>
                        <td>
                            <vc-date-picker
                                    v-model="endDate"
                                    :min-date="startDate"
                                    :max-date="new Date()"
                                    color="gray"
                            >
                                <template v-slot="{ inputValue, inputEvents }">
                                    <v-text-field
                                            class="mt-2 description"
                                            :value="inputValue ? `${inputValue}` : null"
                                            v-on="inputEvents"
                                            dense
                                            outlined
                                    />
                                </template>
                            </vc-date-picker>
                        </td>
                        <td>
                            <v-select
                                    v-model="selectedStatus"
                                    class="mt-2"
                                    dense
                                    outlined
                                    :items="paymentStatuses"
                                    item-text="text"
                                    item-value="name"
                                    @change="updateField($event, 'status')"
                            />
                        </td>
                        <td>
                            <v-text-field
                                    class="mt-2"
                                    dense
                                    outlined
                                    @input="updateField($event, 'company_name')"
                            />
                        </td>
                        <td>
                            <v-select
                                    v-model="selectedBank"
                                    class="mt-2"
                                    dense
                                    outlined
                                    :items="bank_names"
                                    item-text="name"
                                    item-value="swift"
                                    @change="updateField($event, 'swift')"
                            />
                        </td>
                    </tr>
                    </tbody>
                </template>
            </v-simple-table>
            <v-data-table
                    :footer-props="footerProps"
                    :headers="tableHeaders"
                    :items="bulkPaymentsHistory"
                    :server-items-length="bulkPaymentHistoryTotalCount"
                    :options.sync="options"
                    :items-per-page="100"
                    class="payments-table"
                    dense
            >
                <template v-slot:item="{ item }">
                    <tr
                            :key="`bulk_payment_${item.id}`"
                            :class="{ activeTableRow: activeBulkPayment && parseInt(item.id) === parseInt(activeBulkPayment.id)}"
                            @click="handleTableRowClick(item)"
                    >
                        <td>{{ item.created_at ? tz(item.created_at) : "-" }}</td>
                        <td>{{ `${(Math.round(item.amount * 100) / 100).toFixed(2)}€` }}</td>
                        <td style="text-align: end">
                            <span :style="{ 'background-color': getStatusColor(item.status) }" class="dot"></span>
                        </td>
                        <td style="text-align: end" v-if="user.role === 'admin'">
                            {{ item.company_name ? item.company_name : "-" }}
                        </td>
                    </tr>
                </template>
            </v-data-table>
        </v-card>
        <v-navigation-drawer :value="drawerOpen" fixed hide-overlay right width="900">
            <bulk-payment-history-view v-if="$route.name === 'bulk-payment-history-view'"></bulk-payment-history-view>
        </v-navigation-drawer>
    </div>
</template>
<script>
import {mapActions, mapMutations, mapState} from "vuex";
import BulkPaymentHistoryView from "../bulk-payment-history/bulk-payment-history-view";
import moment from "moment";

export default {
    name: "bulk-payment-history-list",
    components: {BulkPaymentHistoryView},
    data() {
        return {
            tableHeaders: [
                {
                    text: this.t("Created at"),
                    align: "left",
                    value: "created_at"
                },
                {
                    text: this.t("Amount"),
                    align: "left",
                    value: "amount"
                },
                {
                    text: this.t("Status"),
                    align: "end",
                    value: "status"
                },
            ],
            pageLoaded: false,
            selectedStatus: null,
            selectedBank: null,
            footerProps: {"items-per-page-options": [25, 50, 100, 500, 1000]},
            paymentStatuses: [
                {text: "All statuses", name: ""},
                {text: "Finished", name: "finished"},
                {text: "Started", name: "started"},
                {text: "Waiting", name: "waiting"},
                {text: "Cancelled", name: "cancelled"},
                {text: "Expired", name: "expired"},
                { text: "Inactive", name: "inactive" },
            ],
            mainDrawerOpen: null,
            options: this.bulkPaymentHistoryTableOptions,
            startDate: moment()
                    .subtract(1, "weeks")
                    .toDate(),
            endDate: moment().toDate()
        };
    },
    computed: {
        ...mapState([
            "user",
            "bank_names",
            "drawerOpen",
            "requestError",
            "errorMessage",
            "requestSuccess",
        ]),
        ...mapState('bulkPaymentHistory', ['bulkPaymentsHistory', 'activeBulkPayment', 'loadingBulkPaymentsHistoryList',
            'loadingBulkPaymentData', 'bulkPaymentHistoryTableOptions', 'bulkPaymentsHistorySearch', 'bulkPaymentHistoryTotalCount'])
    },
    mounted() {
        document.addEventListener("keydown", this.handleKeyboardListener);
    },
    async beforeCreate() {
        const drawerState = await localStorage.getItem("mainDrawerOpen");
        if (drawerState) {
            this.mainDrawerOpen = JSON.parse(drawerState);
        } else {
            this.mainDrawerOpen = true;
        }
        this.$nextTick(async () => {
            if (this.user && this.user.role === "admin") {
                await this.tableHeaders.push(
                        {
                            text: this.t("Company"),
                            align: "end",
                            value: "company_name"
                        }
                );
            }
        });
        if (this.$route.params.start_date) {
            let data = {
                start_date: this.$route.params.start_date,
                end_date: this.$route.params.end_date,
                status: this.$route.params.status,
                bank_name: this.$route.params.bank_name
            };
            this.selectedStatus = data.status;
            for (let i = 0; i < this.bank_names.length; i++) {
                if (data.bank_name === this.bank_names[i].name) {
                    this.selectedBank = this.bank_names[i].swift;
                    break;
                }
            }
            await this.updateBulkPaymentsHistoryDateSearch(data);
            await this.$router.replace({query: null});
        } else {
            let data = {
                start_date: moment(this.startDate).format("YYYY-MM-DD"),
                end_date: moment(this.endDate).format("YYYY-MM-DD")
            };
            await this.updateBulkPaymentsHistoryDateSearch(data);
        }
        await this.getBulkPaymentsHistory();
        this.pageLoaded = true;
    },
    async destroyed() {
        document.removeEventListener("keydown", this.handleKeyboardListener);
        await this.resetBulkPaymentsHistorySearch();
    },
    watch: {
        endDate: async function () {
            let data = {
                start_date: moment(this.startDate).format("YYYY-MM-DD"),
                end_date: moment(this.endDate).format("YYYY-MM-DD"),
                status: this.bulkPaymentsHistorySearch.status
            };
            await this.updateBulkPaymentsHistoryDateSearch(data);
            await this.getBulkPaymentsHistory();
        },
        startDate: async function () {
            let data = {
                start_date: moment(this.startDate).format("YYYY-MM-DD"),
                end_date: moment(this.endDate).format("YYYY-MM-DD"),
                status: this.bulkPaymentsHistorySearch.status
            };
            await this.updateBulkPaymentsHistoryDateSearch(data);
            await this.getBulkPaymentsHistory();
        },
        async options(val) {
            await this.setBulkPaymentHistoryTableOptions(val);
            if (this.pageLoaded) await this.getBulkPaymentsHistory();
        }
    },
    methods: {
        ...mapMutations('bulkPaymentHistory', ['updateBulkPaymentsHistoryDateSearch',
            'setBulkPaymentHistoryTableOptions', 'resetBulkPaymentsHistorySearch', 'updateBulkPaymentsHistorySearch']),
        ...mapActions('bulkPaymentHistory', ['getBulkPaymentsHistory', 'getBulkPaymentData']),
        async handleTableRowClick(payment) {
            if (this.$route.name !== "bulk-payment-history-view") {
                await this.$router.push({
                    name: "bulk-payment-history-view",
                    params: {paymentId: payment.id}
                });
                return;
            }
            if (
                    this.$route.name === "bulk-payment-history-view" &&
                    parseInt(payment.id) !== parseInt(this.$route.params.paymentId)
            ) {
                await this.getBulkPaymentData({paymentId: payment.id});
                await this.$router.push({
                    name: "bulk-payment-history-view",
                    params: {paymentId: payment.id}
                });
            }
        },
        getStatusColor(status) {
            if (status === "finished") {
                return "#4CAF50";
            } else if (["failed", "cancelled"].includes(status)) {
                return "#FF0000";
            } else if (status === "waiting") {
                return "#999999";
            } else if (status === "expired") {
                return "#0066ff";
            } else if (status === "started") {
                return "#eec01f";
            } else if (status === "inactive") {
                return "#af00ac";
            } else if (status === "partial") {
                return "#c46600";
            } else {
                return "#999999";
            }
        },
        async handleKeyboardListener(e) {
            if (e.keyCode === 27 && this.$route.name === "bulk-payment-history-view") {
                this.$router.push({name: "bulk-payments-history-list"});
            }
            if (this.activeBulkPayment && Object.keys(this.activeBulkPayment).length > 0) {
                let currentProductIndex = this.bulkPaymentsHistory.findIndex(p => p.id === this.activeBulkPayment.id);
                if (e.key === "ArrowUp") {
                    if (currentProductIndex !== 0) {
                        const newPayment = this.bulkPaymentsHistory[currentProductIndex - 1];
                        await this.getBulkPaymentData({
                            paymentId: newPayment.id,
                        });
                        await this.$router.push({
                            name: this.$route.name,
                            params: { paymentId: newPayment.id}
                        });
                    }
                } else if (e.key === "ArrowDown") {
                    if (currentProductIndex + 1 !== this.bulkPaymentsHistory.length) {
                        const newPayment = this.bulkPaymentsHistory[currentProductIndex + 1];
                        await this.getBulkPaymentData( {
                            paymentId: newPayment.id,
                        });
                        await this.$router.push({
                            name: this.$route.name,
                            params: {paymentId: newPayment.id}
                        });
                    }
                }
            }
        },
        async updateField(value, field) {
            await this.updateBulkPaymentsHistorySearch({prop: field, value: value});
            await this.getBulkPaymentsHistory();
        }
    }
};
</script>
<style scoped>
.activeTableRow {
    background-color: #707070;
    color: #fff;
}

.activeTableRow:hover {
    background-color: #8d8d8d !important;
    color: #fff !important;
}

.dot {
    margin-top: 4px;
    height: 15px;
    width: 15px;
    border-radius: 50%;
    display: inline-block;
}

.description {
    word-wrap: anywhere;
}

#search tr:hover {
    background-color: transparent !important;
}
</style>
